import type { IntlShape } from "@formatjs/intl";
import { Button, Form, Skeleton } from "antd";
import { useEffect, useMemo } from "react";
import { connect, type DispatchProp } from "react-redux";
import { actions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import ModalFooter from "../../common/components/ModalFooter";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { filterDataForDispatch } from "../../common/functions";
import { routeNames } from "../common/routes.ts";
import { ticketDataActions } from "../../features/ticket-data/ticketDataSlice";
import { turbineActions } from "../../features/turbine/turbineSlice";
import type {
	Client,
	Location,
	TurbineRetrieve,
} from "../generated-axios-client/index.ts";
import { TurbineFields } from "./TurbineFields";

const fields = new Set(["client", "location"]);

type CreateTurbineFormProps = {
	initialValue: TurbineRetrieve | null;
	intl: IntlShape;
	clientItems?: Client[];
	locationItems?: Location[];
	currentRoute: RouterState | null;
} & DispatchProp;

export function CreateTurbineForm(props: CreateTurbineFormProps) {
	const {
		dispatch,
		initialValue,
		intl,
		clientItems,
		locationItems,
		currentRoute,
	} = props;

	const [form] = Form.useForm();

	const isEdit = !!initialValue?.id;

	const saveTurbine = () => {
		form
			.validateFields()
			.then((values) => {
				const editedValues = { ...values };
				if (initialValue?.id) {
					editedValues.id = initialValue.id;
				}
				dispatch(turbineActions.addOrUpdateTurbine(editedValues));
			})
			.catch(() => {});
	};

	const initialValues = useMemo(() => {
		return initialValue
			? {
					...initialValue,
					client: initialValue.client.id,
					location: initialValue.location.id,
				}
			: undefined;
	}, [initialValue]);

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		} else {
			form.resetFields();
		}
	}, [initialValues, form]);

	const relevantFieldsChanged = (changedValues: Record<string, unknown>) => {
		for (const key of Object.keys(changedValues)) {
			if (fields.has(key)) {
				dispatch(
					ticketDataActions.fetchTicketData(
						filterDataForDispatch(changedValues),
					),
				);
			}
		}
	};

	if (!clientItems || !locationItems) {
		return <Skeleton active />;
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({
					id: isEdit ? "turbine_edit_title" : "turbine_create_title",
				})}
				subtitle={
					isEdit
						? undefined
						: intl.formatMessage({ id: "turbine_create_subtitle" })
				}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={initialValues}
					// onValuesChange={(changedValues) => relevantFieldsChanged(changedValues)}
				>
					<TurbineFields
						intl={intl}
						clientItems={clientItems}
						locationItems={locationItems}
						form={form}
					/>
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								actions.navigateTo(
									isEdit ? routeNames.TURBINE_VIEW : routeNames.TURBINE_LIST,
									isEdit ? { id: initialValue!.id } : undefined,
								),
							)
						}
					>
						{intl.formatMessage({ id: "cancel" })}
					</Button>
					<Button type="primary" onClick={saveTurbine}>
						{intl.formatMessage({ id: "confirm" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const CreateTurbineFormConnected = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	clientItems: state.ticketData.clients,
	initialValue: state.turbine.turbine,
	locationItems: state.ticketData.locations,
	currentRoute: state.router.route,
}))(CreateTurbineForm);

export default CreateTurbineFormConnected;
